import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, Clock, MessageCircle, Zap, Target } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

const OrganicImage = ({ src, alt, className = "" }) => (
  <div className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`} style={{ WebkitMaskImage: 'url(/images/mood/shape-mask.png)', maskImage: 'url(/images/mood/shape-mask.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }}>
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function ConsultationPage() {
  return (
    <main data-testid="consultation-page">

      {/* 1. HERO */}
      <section className="pt-32 pb-20 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">A QUICK CONVERSATION</span>
            <h1 className="mb-6">
              <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">1:1 CONSULTATION</span>
              <span className="font-accent text-2xl md:text-3xl lg:text-4xl text-[#BE123C] block leading-[1.1] mt-2">90 minutes on whatever is blocking your brand right now</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 leading-relaxed max-w-xl mb-10">
              You bring the question. I bring 15 years of brand strategy, creative direction and leading teams of 40+ people across Europe. I have coached founders through pivots, pricing decisions and identity crises. We sit down for 90 minutes and work through whatever is stuck. You leave with clarity and a concrete next step.
            </p>
            <div className="flex flex-wrap gap-4 mb-10">
              {["90 minutes", "Video call", "Recording included", "Follow-up notes"].map((label, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-white/5 border border-white/10 px-4 py-2">
                  <Check size={12} className="text-[#D4A0A0]" />
                  <span className="font-body text-xs text-white/60 tracking-wide">{label}</span>
                </div>
              ))}
            </div>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
              BOOK YOUR CONSULTATION <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. THE REAL PROBLEM */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE REAL PROBLEM</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">YOU KNOW SOMETHING IS OFF</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">but you cannot name it yet</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>You have been thinking about your brand for weeks. Maybe months. You know something needs to change but you cannot pinpoint what. You go back and forth between ideas, read things online, ask friends for opinions and end up more confused than before.</p>
                <p>Sometimes all you need is 90 minutes with someone who has seen hundreds of brands from the inside out. Someone who can name what is off within the first 15 minutes and spend the rest of the time figuring out what to do about it. That is what this call is for.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center">
              <OrganicImage src="/images/ola-consultation.jpg" alt="Aleksandra Kałuża" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. WHAT PEOPLE BRING */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">COMMON TOPICS</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WHAT FOUNDERS USUALLY</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">bring to these calls</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { Icon: Target, title: "Positioning is unclear", body: "You know what you do but you cannot explain it in a way that makes people lean in. We find the one sentence that lands." },
              { Icon: MessageCircle, title: "Messaging feels generic", body: "Your website and Instagram sound like every other business in your niche. We figure out what is actually different about you and how to say it." },
              { Icon: Zap, title: "Something feels off but you cannot name it", body: "The brand looks okay on paper but something is not clicking. I can usually spot it within the first 15 minutes." },
              { Icon: Clock, title: "You are about to make a big decision", body: "A rebrand, a pivot, a price increase, a new offer. You want someone with experience to look at it before you commit." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="flex items-start gap-4 bg-white p-6 border border-[#e5e2de]">
                <item.Icon size={20} className="text-[#BE123C] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-body text-sm font-semibold text-[#1C1210] mb-1">{item.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{item.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">HOW IT WORKS</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">SIMPLE PROCESS</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">three steps</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              { step: "01", title: "Book the call", body: "Pick a time that works for you. Before the call, I will ask you to fill in a short form so I come prepared." },
              { step: "02", title: "We talk for 90 minutes", body: "We get into the real issue. I listen, I ask questions, I tell you what I see. We work through it together and land on a clear direction." },
              { step: "03", title: "You get the recording and notes", body: "After the call, you receive a recording and my written summary with concrete next steps. No fluff, just what to do." },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="border-t border-[#1C1210]/15 pt-6">
                <span className="font-display text-4xl text-[#BE123C] block mb-4 leading-none">{s.step}</span>
                <h3 className="font-serif text-xl text-[#1C1210] mb-3">{s.title}</h3>
                <p className="font-body text-sm text-[#6B6560] leading-[1.7]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. SELF-SELECT with links */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-[#8B1A3A] mb-6 block">IS THIS THE RIGHT FIT</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 border border-[#e5e2de]">
                <h3 className="font-serif text-lg text-[#1C1210] mb-4">This call is for you if</h3>
                <ul className="space-y-3">
                  {[
                    "You have a specific brand question that needs an experienced eye",
                    "You want clarity before investing in a bigger project",
                    "You are stuck on a decision and you need someone to help you think it through",
                    "You want honest feedback on your current brand presence",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={14} className="text-[#BE123C] flex-shrink-0 mt-1" />
                      <span className="font-body text-sm text-[#4A4540] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white p-6 border border-[#e5e2de]">
                <h3 className="font-serif text-lg text-[#1C1210] mb-4">This is not the right fit if</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">You want a complete brand strategy. Look at <Link to="/brand-foundation" className="text-[#BE123C] underline hover:text-[#9F1239]">Brand Foundation</Link> instead.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">You need design deliverables. Look at <Link to="/visual-identity" className="text-[#BE123C] underline hover:text-[#9F1239]">Full Brand System</Link>.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#9A9590] flex-shrink-0 mt-2" />
                    <span className="font-body text-sm text-[#6B6560] leading-relaxed">You want ongoing coaching sessions. I do single consultations, not recurring calls.</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="bg-[#f5f5f7] p-8 md:p-12 relative border border-[#e5e2de]">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Before, I did not see much value in what I was doing. I was showing up for the paycheque, not the people.</p>
                <p>These workshops showed me what I actually offer, what my real strengths are and what I truly want to represent. My edge was never technical knowledge. It was my personality and the way I approach people.</p>
                <p>The work has colour again. Now I wake up eager to go to work, and I had not felt that in a long time.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Mortgage broker, Brand Foundation client</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. FINAL CTA */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="font-eyebrow text-white mb-6 block">READY WHEN YOU ARE</span>
            <h2 className="mb-6">
              <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">90 MINUTES</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">can change the direction of your brand</span>
            </h2>
            <p className="font-body text-base text-white/70 leading-relaxed mb-10">
              You do not need to have everything figured out. You just need one good conversation with someone who knows what to look for. Book a time and let's get into it.
            </p>
            <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
              BOOK YOUR CONSULTATION <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
